import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  wards: [],
  patients: [],
  beds: [],
  floorPlans: {},
  selectedWardId: null,
};

const bedManagementSlice = createSlice({
  name: "bedManagement",
  initialState,
  reducers: {
    addWard: (state, action) => {
      const newWard = {
        id: Date.now(),
        name: action.payload.name,
        createdAt: new Date().toISOString(),
      };
      state.wards.push(newWard);
    },
    addPatient: (state, action) => {
      state.patients.push({
        id: Date.now(),
        name: action.payload.name,
        wardId: action.payload.wardId,
        bedId: action.payload.bedId || null,
        dateOfBirth: action.payload.dateOfBirth || "",
        gender: action.payload.gender || "Male",
        fileNo: action.payload.fileNo || "",
        phoneNo: action.payload.phoneNo || "",
        priority: action.payload.priority || "Normal",
        reason: action.payload.reason || "",
        status: action.payload.status || "Active",
        waitingTime: action.payload.waitingTime || 0,
        admissionDate: new Date().toISOString(),
        createdAt: new Date().toISOString(),
      });
    },
    updatePatient: (state, action) => {
      const index = state.patients.findIndex((p) => p.id === action.payload.id);
      if (index !== -1) {
        state.patients[index] = { ...state.patients[index], ...action.payload };
      }
    },
    addBed: (state, action) => {
      state.beds.push({
        id: action.payload.id || Date.now(),
        name: action.payload.name,
        wardId: action.payload.wardId,
        availability: action.payload.availability,
        patientId: action.payload.patientId || null,
        position: {
          x: action.payload.x,
          y: action.payload.y,
        },
        rotation: 0, // Default rotation
        createdAt: new Date().toISOString(),
      });
    },
    updateBed: (state, action) => {
      const index = state.beds.findIndex((b) => b.id === action.payload.id);
      if (index !== -1) {
        state.beds[index] = { ...state.beds[index], ...action.payload };
      }
    },
    updateBedPosition: (state, action) => {
      const index = state.beds.findIndex((b) => b.id === action.payload.id);
      if (index !== -1) {
        state.beds[index].position = action.payload.position;
      }
    },
    rotateBed: (state, action) => {
      const index = state.beds.findIndex((b) => b.id === action.payload);
      if (index !== -1) {
        const currentRotation = state.beds[index].rotation || 0;
        state.beds[index].rotation = currentRotation === 0 ? 90 : 0;
      }
    },
    setFloorPlan: (state, action) => {
      state.floorPlans[action.payload.wardId] = action.payload.floorPlan;
    },
    deleteWard: (state, action) => {
      state.wards = state.wards.filter((w) => w.id !== action.payload);
      // Also remove beds and reassign patients
      state.beds = state.beds.filter((b) => b.wardId !== action.payload);
      state.patients = state.patients.map((p) =>
        p.wardId === action.payload ? { ...p, wardId: null, bedId: null } : p
      );
      delete state.floorPlans[action.payload];
    },
    deleteBed: (state, action) => {
      state.beds = state.beds.filter((b) => b.id !== action.payload);
      // Unassign patients from this bed
      state.patients = state.patients.map((p) =>
        p.bedId === action.payload ? { ...p, bedId: null } : p
      );
    },
    deletePatient: (state, action) => {
      const patientToDelete = state.patients.find(
        (p) => p.id === action.payload
      );
      if (patientToDelete && patientToDelete.bedId) {
        // If patient had a bed, make it available
        const bedIndex = state.beds.findIndex(
          (b) => b.patientId === patientToDelete.id
        );
        if (bedIndex !== -1) {
          state.beds[bedIndex].patientId = null;
          state.beds[bedIndex].availability = "Available";
        }
      }
      state.patients = state.patients.filter((p) => p.id !== action.payload);
    },
    setSelectedWard: (state, action) => {
      state.selectedWardId = action.payload;
    },
  },
});

export const {
  addWard,
  addPatient,
  updatePatient,
  addBed,
  updateBed,
  updateBedPosition,
  rotateBed,
  setFloorPlan,
  deleteWard,
  deleteBed,
  deletePatient,
  setSelectedWard,
} = bedManagementSlice.actions;

export default bedManagementSlice.reducer;
